import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api, resolveImg } from "@/lib/adminApi";

export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus("loading");
    api
      .get(`/articles/${slug}`)
      .then((res) => { setArticle(res.data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") {
    return <div className="min-h-screen flex items-center justify-center bg-bg text-bone eyebrow grain vignette">Pouring the page…</div>;
  }

  if (status === "notfound") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-bg text-bone grain vignette px-6 text-center">
        <h1 className="font-display text-5xl">Not on the shelf.</h1>
        <p className="font-body text-lg text-bone-dim">This story may be a draft, or moved.</p>
        <button onClick={() => navigate("/")} className="btn-outline">Back Home</button>
      </div>
    );
  }

  return (
    <div className="grain vignette bg-bg text-bone min-h-screen">
      <header className="border-b sticky top-0 z-30 nav-blur" style={{ borderColor: "var(--hairline)" }}>
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="article-logo">
            <svg width="26" height="26" viewBox="0 0 40 40" fill="none">
              <path d="M8 8 L20 32 L32 8" stroke="var(--brass)" strokeWidth="1.4" />
              <circle cx="20" cy="20" r="3" fill="var(--amber)" />
            </svg>
            <span className="font-display text-[1.1rem] tracking-wide">Johnny Dram</span>
          </Link>
          <Link to="/#journal" className="btn-outline" data-testid="article-back"><ArrowLeft size={16} /> The Journal</Link>
        </div>
      </header>

      <article className="max-w-[820px] mx-auto px-6 sm:px-8 py-16 md:py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="flex items-center gap-3 mb-6">
            {article.chapter_no && <span className="chapter-numeral text-3xl">{article.chapter_no}</span>}
            <span className="eyebrow">{article.category}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-bone" data-testid="article-title">
            {article.title}
          </h1>
          {article.dek && (
            <p className="font-display italic text-xl sm:text-2xl text-bone-dim mt-6 leading-snug">{article.dek}</p>
          )}
          <div className="flex items-center gap-4 mt-8 pb-8 border-b" style={{ borderColor: "var(--hairline)" }}>
            <span className="eyebrow">By {article.author || "Johnny Dram"}</span>
            <span className="w-1 h-1 rounded-full" style={{ background: "var(--brass)" }} />
            <span className="eyebrow" style={{ color: "var(--bone-dim)" }}>{article.reading_time}</span>
          </div>
        </motion.div>

        {article.image_url && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="my-12 tile-frame"
          >
            <img src={resolveImg(article.image_url)} alt={article.title} className="w-full h-auto object-cover" />
          </motion.div>
        )}

        <div
          className="jd-prose"
          data-testid="article-body"
          dangerouslySetInnerHTML={{ __html: article.body || "" }}
        />

        <div className="mt-16 pt-8 border-t flex items-center justify-between" style={{ borderColor: "var(--hairline)" }}>
          <Link to="/#journal" className="btn-outline" data-testid="article-more"><ArrowLeft size={16} /> More Essays</Link>
          <span className="eyebrow" style={{ color: "var(--bone-dim)" }}>Johnny Dram</span>
        </div>
      </article>
    </div>
  );
}
